import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ArrowLeft, Activity, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatNumber, formatCompactNumber } from '../utils/formatters';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

function VideoPerformanceAnalysis() {
  const { influencerName } = useParams();
  const navigate = useNavigate();
  const [performanceData, setPerformanceData] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState('all');
  const [dateRange, setDateRange] = useState('7d'); // "24h", "3d", "7d", "14d", "30d", "all"
  const [loading, setLoading] = useState(true);
  const [deletingVideoId, setDeletingVideoId] = useState(null);
  const [intervalTrackingData, setIntervalTrackingData] = useState([]);

  useEffect(() => {
    fetchVideoPerformance();
    fetchIntervalTracking();
  }, [influencerName, dateRange]);

  const fetchVideoPerformance = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/influencers/${influencerName}/video-performance`, {
        params: {
          time_range: dateRange
        }
      });
      setPerformanceData(response.data);
    } catch (error) {
      console.error('Error fetching video performance:', error);
      toast.error('Failed to load video performance data');
    } finally {
      setLoading(false);
    }
  };
  const fetchIntervalTracking = async () => {
    try {
      const response = await axios.get(`${API}/videos/interval-tracking`, {
        params: {
          influencer_name: influencerName
        }
      });
      setIntervalTrackingData(response.data);
    } catch (error) {
      console.error('Error fetching interval tracking:', error);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    try {
      setDeletingVideoId(videoId);
      await axios.delete(`${API}/videos/${videoId}`);
      toast.success('Video deleted successfully');
      // Refresh the data
      await fetchVideoPerformance();
      // If the deleted video was selected, reset to 'all'
      if (selectedVideo === videoId) {
        setSelectedVideo('all');
      }
    } catch (error) {
      toast.error('Failed to delete video');
      console.error('Error deleting video:', error);
    } finally {
      setDeletingVideoId(null);
    }
  };

  if (loading || !performanceData) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="performance-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const videoData = performanceData.videos || [];
  const graph1Data = performanceData.graph1_data || [];
  const graph2Data = performanceData.graph2_data || [];

  // Prepare data for Graph 1: Views Growth (15-min intervals) - using graph1_data from backend
  const prepareViewsGrowthData = () => {
    if (selectedVideo === 'all') {
      // Combine all videos' timeseries
      const allTimestamps = new Set();
      graph1Data.forEach(video => {
        video.timeseries.forEach(metric => {
          allTimestamps.add(metric.timestamp);
        });
      });

      const timestamps = Array.from(allTimestamps).sort();
      
      return timestamps.map(timestamp => {
        const dataPoint = { timestamp };
        graph1Data.forEach((video, index) => {
          const metric = video.timeseries.find(m => m.timestamp === timestamp);
          dataPoint[`video_${index}`] = metric ? metric.views : null;
        });
        return dataPoint;
      });
    } else {
      // Single video data
      const video = graph1Data.find(v => v.video_id === selectedVideo);
      if (!video) return [];
      
      return video.timeseries.map(metric => ({
        timestamp: metric.timestamp,
        views: metric.views
      }));
    }
  };

  // Prepare data for Graph 2: Views vs Signups (Daily) - using graph2_data from backend
  const prepareCorrelationData = () => {
    // Backend already provides the aggregated daily data
    return graph2Data;
  };

  const viewsGrowthData = prepareViewsGrowthData();
  const correlationData = prepareCorrelationData();

  return (
    <div className="space-y-6" data-testid="video-performance-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/rankings')}
            variant="outline"
            data-testid="back-button"
            className="border-white/20 text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white" data-testid="performance-title">
              Video Performance Analysis: {influencerName}
            </h1>
            <p className="text-gray-400 mt-1">Detailed metrics and conversion tracking</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <label className="text-gray-300 text-sm font-medium">Video Filter:</label>
            <Select value={selectedVideo} onValueChange={setSelectedVideo}>
              <SelectTrigger data-testid="video-filter" className="w-64 bg-white/5 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1f3a] border-white/10 text-white">
                <SelectItem value="all">
                  All Videos ({videoData.length})
                </SelectItem>
                {videoData.map((video, index) => (
                  <SelectItem key={video.id} value={video.id}>
                    Video {index + 1}: {formatCompactNumber(video.current_views)} views
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center space-x-4">
            <label className="text-gray-300 text-sm font-medium">Time Range:</label>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger data-testid="date-range-filter" className="w-48 bg-white/5 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1f3a] border-white/10 text-white">
                <SelectItem value="24h">Last 24 Hours</SelectItem>
                <SelectItem value="3d">Last 3 Days</SelectItem>
                <SelectItem value="7d">Last 7 Days</SelectItem>
                <SelectItem value="14d">Last 14 Days</SelectItem>
                <SelectItem value="30d">Last 30 Days</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Graph 1: Views Growth Over Time */}
      <div className="glass-card rounded-xl p-6" data-testid="views-growth-chart">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">
            Views Growth Over Time (15-min intervals)
          </h2>
          
          {/* Peak Growth Rate Info */}
          {graph1Data.length > 0 && (selectedVideo !== 'all' || graph1Data.length === 1) && (
            <div className="text-right">
              {(() => {
                const video = selectedVideo !== 'all' 
                  ? graph1Data.find(v => v.video_id === selectedVideo)
                  : graph1Data[0];  // If only 1 video, show its peak
                
                if (video && video.peak_growth_rate > 0) {
                  return (
                    <div className="text-sm">
                      <div className="text-gray-400">Peak Growth Rate:</div>
                      <div className="text-green-400 font-bold text-lg">
                        {formatNumber(video.peak_growth_rate)} views/15min
                      </div>
                      {video.peak_growth_timestamp && (
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(video.peak_growth_timestamp).toLocaleString()}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={viewsGrowthData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="timestamp" 
              stroke="#9ca3af"
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
              }}
            />
            <YAxis stroke="#9ca3af" />
            <Tooltip
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
              labelStyle={{ color: '#f3f4f6' }}
              labelFormatter={(value) => new Date(value).toLocaleString()}
              formatter={(value) => formatNumber(value)}
            />
            <Legend />
            {selectedVideo === 'all' ? (
              graph1Data.map((video, index) => (
                <Line
                  key={video.video_id}
                  type="monotone"
                  dataKey={`video_${index}`}
                  stroke={COLORS[index % COLORS.length]}
                  name={video.video_label}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))
            ) : (
              <Line
                type="monotone"
                dataKey="views"
                stroke="#3b82f6"
                name="Views"
                strokeWidth={3}
                dot={{ r: 3 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Graph 2: Views vs Signups Correlation */}
      <div className="glass-card rounded-xl p-6" data-testid="correlation-chart">
        <h2 className="text-xl font-semibold text-white mb-4">
          Views vs Signups Correlation (Daily)
        </h2>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={correlationData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="date" 
              stroke="#9ca3af"
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            <YAxis yAxisId="left" stroke="#3b82f6" />
            <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
            <Tooltip
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
              labelStyle={{ color: '#f3f4f6' }}
              labelFormatter={(value) => new Date(value).toLocaleDateString()}
              formatter={(value) => formatNumber(value)}
            />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="views"
              stroke="#3b82f6"
              name="Total Views"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="signups"
              stroke="#10b981"
              name="Signups"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Video Details Table */}
      <div className="glass-card rounded-xl p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Video Details</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="text-left text-gray-400 text-sm border-b border-white/10">
              <tr>
                <th className="p-3">Video</th>
                <th className="p-3">Platform</th>
                <th className="p-3">Current Views</th>
                <th className="p-3">Status</th>
                <th className="p-3">Signups</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {videoData.map((video, index) => (
                <tr key={video.id} className="border-b border-white/5">
                  <td className="p-3 text-white">Video {index + 1}</td>
                  <td className="p-3">
                    <a 
                      href={video.content_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-colors"
                    >
                      <span>{video.platform}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="p-3 text-white">{formatNumber(video.current_views)}</td>
                  <td className="p-3">
                    {video.status === 'ACTIVE' && (
                      <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-300">🟢 ACTIVE</span>
                    )}
                    {video.status === 'DECLINING' && (
                      <span className="px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-300">🟡 DECLINING</span>
                    )}
                    {video.status === 'DEAD' && (
                      <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-300">🔴 DEAD</span>
                    )}
                  </td>
                  <td className="p-3 text-white">
                    {formatNumber(video.total_signups || 0)}
                  </td>
                  <td className="p-3">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-gray-900 border-gray-700">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete Video</AlertDialogTitle>
                          <AlertDialogDescription className="text-gray-400">
                            Are you sure you want to delete Video {index + 1}? This will also delete all associated metrics and cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-gray-800 text-white hover:bg-gray-700">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteVideo(video.id)}
                            disabled={deletingVideoId === video.id}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            {deletingVideoId === video.video_id ? 'Deleting...' : 'Delete'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Interval Tracking Table */}
      <div className="glass-card rounded-xl p-6">
        <h2 className="text-2xl font-bold text-white mb-6">15-Minute Interval Tracking</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="p-3 text-left text-sm font-semibold text-gray-300 sticky left-0 bg-[#0f1638] z-10">Video</th>
                <th className="p-3 text-left text-sm font-semibold text-gray-300">Go Live</th>
                {intervalTrackingData.length > 0 && intervalTrackingData[0].interval_tracking && 
                  intervalTrackingData[0].interval_tracking.map((interval, idx) => (
                    <th key={idx} className="p-3 text-center text-sm font-semibold text-gray-300 min-w-[150px]">
                      {new Date(interval.timestamp).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      })} ({new Date(interval.timestamp).toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric'
                      })})
                    </th>
                  ))
                }
              </tr>
            </thead>
            <tbody>
              {intervalTrackingData.map((video, index) => (
                <tr key={video.id} className="border-b border-white/5">
                  <td className="p-3 sticky left-0 bg-[#0f1638] z-10">
                    <a 
                      href={video.content_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                    >
                      <span>Video {index + 1}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="p-3 text-gray-300 text-sm whitespace-nowrap">
                    {video.go_live_timestamp ? new Date(video.go_live_timestamp).toLocaleString() : 'N/A'}
                  </td>
                  {video.interval_tracking && video.interval_tracking.map((interval, idx) => (
                    <td key={idx} className="p-3 text-center text-white">
                      {formatNumber(interval.views)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default VideoPerformanceAnalysis;
